/** A provider as the billing documents carry it: a practice doctor (userId) or a saved document's Provider (id). */
export interface BillingProvider {
  id?: string;
  userId?: string;
  title?: string | null;
  name?: string | null;
  surname?: string | null;
  mpNumber?: string | null;
}

/** "Dr Anna Botha (MP 0123456)": title, name, surname and the HPCSA/MP number, leaving out whatever is missing. */
export function providerLabel(provider: BillingProvider | null | undefined): string {
  if (!provider) return '';
  const name = [provider.title, provider.name, provider.surname].map(p => (p || '').trim()).filter(Boolean).join(' ');
  const mp = (provider.mpNumber || '').trim();
  if (!mp) return name;
  return `${name} (${/^mp/i.test(mp) ? mp : `MP ${mp}`})`;
}

/**
 * Who a line was billed for: the provider saved on the line (`Provider`, or `provider` while the form is being
 * printed), else the practice doctor its `providerId` names, else the document's provider.
 */
export function lineProvider(line: any, documentProvider: BillingProvider | null | undefined, providers: BillingProvider[] = []): BillingProvider | null {
  const own = line?.provider || line?.Provider;
  if (own) return own;
  if (line?.providerId) {
    const found = providers.find(p => (p.userId || p.id) === line.providerId);
    if (found) return found;
    if (documentProvider && (documentProvider.userId || documentProvider.id) === line.providerId) return documentProvider;
  }
  return documentProvider || null;
}

/** The provider's id for a line, so lines for different providers are never merged into one. */
export function lineProviderKey(line: any, documentProvider: BillingProvider | null | undefined, providers: BillingProvider[] = []): string {
  const p = lineProvider(line, documentProvider, providers);
  return p ? String(p.userId || p.id || providerLabel(p)) : '';
}

/** The provider fields a copied line takes with it (estimate to invoice), so it is billed for the same provider. */
export function carriedProvider(line: any): { providerId: string | null; Provider: BillingProvider | null } {
  return { providerId: line?.providerId || null, Provider: line?.Provider || null };
}
